import React, { useState } from 'react';

const joinClasses = (...values) => values.filter(Boolean).join(' ');

const slides = [
  {
    name: 'Ananya Sharma',
    title: 'Head of Product, Bengaluru',
    eyebrow: 'Human-Centric Reliability',
    headline: 'Always-on expertise for your launch velocity.',
    description:
      'Ananya’s team helped us turn a messy rollout into a predictable, polished experience. The operating rhythm became clearer, the handoffs improved, and the whole process felt calm under pressure.',
    imageUrl:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=1200&q=80',
    status: 'Launch Support: Active',
    points: [
      {
        title: 'Proactive mitigation',
        detail: 'We catch blockers early and route around them before users feel the impact.',
      },
      {
        title: 'Clear escalation',
        detail: 'Every issue has a direct owner, so follow-up is faster and easier to trust.',
      },
      {
        title: 'Operator confidence',
        detail: 'The team gets a cleaner, calmer process to work from every day.',
      },
    ],
    metrics: [
      { value: '< 15m', label: 'Avg. response time' },
      { value: '98.4%', label: 'Direct routing' },
    ],
  },
  {
    name: 'Rohan Mehta',
    title: 'Engineering Manager, Mumbai',
    eyebrow: 'Operational Precision',
    headline: 'Reliable execution for fast-moving ops teams.',
    description:
      'Rohan’s squad wanted a single place to track work without losing context. The new flow made daily reviews faster and kept the whole team aligned from first touch to final handoff.',
    imageUrl:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=1200&q=80',
    status: 'Ops Desk: Online',
    points: [
      {
        title: 'Simple handover',
        detail: 'Support, product, and operations stay aligned without context loss.',
      },
      {
        title: 'Cleaner resolution',
        detail: 'Fewer follow-up loops keep the workflow moving from start to finish.',
      },
      {
        title: 'Sharper visibility',
        detail: 'The team can see what changed, when it changed, and why it matters.',
      },
    ],
    metrics: [
      { value: '24/7', label: 'Coverage window' },
      { value: '3x', label: 'Faster triage' },
    ],
  },
  {
    name: 'Kavya Iyer',
    title: 'Data Strategy Lead, Hyderabad',
    eyebrow: 'Data-Backed Support',
    headline: 'Support that scales with your decision making.',
    description:
      'Kavya needed a view that made complex activity feel readable. This setup gave the team a steadier signal, cleaner context, and more confidence when prioritizing the next move.',
    imageUrl:
      'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=1200&q=80',
    status: 'Insight Stream: Live',
    points: [
      {
        title: 'Better visibility',
        detail: 'Patterns stand out faster when the data is easier to scan.',
      },
      {
        title: 'Cleaner signal',
        detail: 'Follow-up and reporting both get a more dependable working view.',
      },
      {
        title: 'Presentation ready',
        detail: 'The result is polished enough to bring directly into stakeholder reviews.',
      },
    ],
    metrics: [
      { value: '99.1%', label: 'Decision clarity' },
      { value: '12', label: 'Teams aligned' },
    ],
  },
];

export function TestimonialCarousel({ className }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentSlide = slides[currentIndex];

  const goNext = () => {
    setCurrentIndex((index) => (index + 1) % slides.length);
  };

  const goPrevious = () => {
    setCurrentIndex((index) => (index - 1 + slides.length) % slides.length);
  };

  return (
    <div className={joinClasses('w-full max-w-[1440px] mx-auto px-4 py-6 lg:py-10', className)}>
      <div className="grid items-center gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
        <div className="relative">
          <div className="relative min-h-[440px] overflow-hidden rounded-[2.5rem] border border-white/80 bg-white shadow-[0_30px_100px_rgba(15,23,42,0.18)] lg:min-h-[520px]">
            <div className="absolute inset-0">
              <img
                src={currentSlide.imageUrl}
                alt={currentSlide.name}
                className="h-full w-full object-cover"
                loading="eager"
                draggable={false}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-white/15 via-white/25 to-white/90" />
              <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.22)_0%,rgba(255,255,255,0.02)_30%,rgba(255,255,255,0.15)_100%)]" />
            </div>

            <div className="absolute left-0 right-0 top-0 p-6 md:p-8">
              <div className="max-w-[70%]">
                <div className="text-sm font-medium text-slate-700/90 md:text-base">{currentSlide.name}</div>
                <div className="text-xs text-slate-500 md:text-sm">{currentSlide.title}</div>
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-900 shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
                  <span className="h-3 w-3 rounded-full bg-emerald-500" />
                  {currentSlide.status}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.42em] text-slate-400">
            <span className="text-slate-500">*</span>
            {currentSlide.eyebrow}
          </div>

          <div className="mt-6">
            <h2 className="max-w-2xl text-4xl font-black tracking-tight text-slate-900 md:text-5xl lg:text-[4.1rem] lg:leading-[1.02]">
              {currentSlide.headline}
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-8 text-slate-600 md:text-lg">
              {currentSlide.description}
            </p>

            <div className="mt-8 grid gap-4 lg:grid-cols-2 lg:gap-5">
              {currentSlide.points.map((point) => (
                <div
                  key={point.title}
                  className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-[0_10px_30px_rgba(15,23,42,0.04)]"
                >
                  <span className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white shadow-sm">
                    ✓
                  </span>
                  <div>
                    <div className="text-base font-semibold text-slate-900 lg:text-lg">{point.title}</div>
                    <div className="text-sm leading-6 text-slate-500 lg:text-base">{point.detail}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              {currentSlide.metrics.map((metric) => (
                <div
                  key={metric.label}
                  className="min-w-[180px] rounded-2xl border border-slate-200 bg-white/90 px-5 py-4 shadow-[0_10px_30px_rgba(15,23,42,0.05)]"
                >
                  <div className="text-3xl font-black tracking-tight text-slate-900">{metric.value}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.22em] text-slate-400">{metric.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={goPrevious}
              aria-label="Previous testimonial"
              className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-900 shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-slate-50"
            >
              ←
            </button>

            <div className="flex items-center gap-2">
              {slides.map((slide, index) => (
                <button
                  key={slide.name}
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Go to ${slide.name}`}
                  className={joinClasses(
                    'h-2.5 rounded-full transition-all duration-300',
                    index === currentIndex ? 'w-10 bg-slate-900' : 'w-2.5 bg-slate-300 hover:bg-slate-400'
                  )}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={goNext}
              aria-label="Next testimonial"
              className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-900 shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-slate-50"
            >
              →
            </button>

            <div className="ml-auto inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 ring-1 ring-emerald-100">
              <span>✓</span>
              {currentSlide.status}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 grid gap-4 md:hidden">
        {slides.map((slide, index) => (
          <button
            key={slide.name}
            type="button"
            onClick={() => setCurrentIndex(index)}
            className={joinClasses(
              'rounded-2xl border px-4 py-3 text-left transition-colors',
              index === currentIndex ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-900'
            )}
          >
            <div className="text-sm font-semibold">{slide.name}</div>
            <div className={joinClasses('mt-1 text-xs', index === currentIndex ? 'text-white/75' : 'text-slate-500')}>
              {slide.title}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

